import React, { useState, useEffect, useCallback, useRef } from "react";
import { Tag, Search, ExternalLink, Info } from "lucide-react";
import api, { apiError, fileUrl, PLACEHOLDER_IMG } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Spinner, DemoTag } from "@/components/common";
import { toast } from "sonner";

const money = (v, cur = "aud") =>
  v == null ? null : `A$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function DealCard({ deal, general }) {
  const ref = useRef(null);
  const seen = useRef(false);
  useEffect(() => {
    if (!ref.current || seen.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !seen.current) {
        seen.current = true;
        api.post(`/deals/${deal.id}/impression`).catch(() => {});
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [deal.id]);

  const disclosure = deal.disclosure || deal.retailer_disclosure || (deal.affiliate ? general : null);
  const isAffiliate = !!(deal.disclosure || deal.retailer_disclosure || deal.affiliate);

  const viewDeal = async () => {
    try {
      const { data } = await api.post(`/deals/${deal.id}/click`);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiError(e, "This deal link isn't available right now.")); }
  };

  const discount = deal.previous_price && deal.price && deal.previous_price > deal.price
    ? Math.round((1 - deal.price / deal.previous_price) * 100) : null;

  return (
    <div ref={ref} data-testid={`deal-card-${deal.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card">
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <img src={fileUrl(deal.image_url) || PLACEHOLDER_IMG} alt={deal.title} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
          {deal.featured && <span className="rounded-md border border-brand-green/40 bg-brand-green/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green">Featured</span>}
          {deal.is_demo && <DemoTag />}
        </div>
        {(deal.deal_text || discount) && (
          <span className="absolute right-2 top-2 rounded-md bg-brand-red px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">
            {deal.deal_text || `${discount}% off`}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3.5">
        <h3 className="line-clamp-2 text-sm font-semibold text-foreground">{deal.title}</h3>
        <p className="text-xs font-medium text-primary">{deal.retailer_name}</p>
        {(deal.price != null || deal.previous_price != null) && (
          <div className="flex items-baseline gap-2">
            {deal.price != null && <span className="font-display text-lg font-extrabold text-foreground">{money(deal.price, deal.currency)}</span>}
            {deal.previous_price != null && deal.previous_price !== deal.price && (
              <span className="text-xs text-muted-foreground line-through">{money(deal.previous_price, deal.currency)}</span>
            )}
          </div>
        )}
        <span className="mt-0.5 w-fit rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{deal.category}</span>
        <div className="mt-auto pt-2">
          <Button className="w-full" onClick={viewDeal} data-testid={`deal-view-${deal.id}`}>
            View Deal <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Button>
          {isAffiliate && (
            <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground" data-testid={`deal-disclosure-${deal.id}`}>
              {disclosure || "Affiliate link"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Deals() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [retailer, setRetailer] = useState("all");

  useEffect(() => { api.get("/deals/meta").then(({ data }) => setMeta(data)).catch(() => setMeta({ categories: [], retailers: [], general_disclosure: "" })); }, []);

  const load = useCallback(() => {
    setItems(null);
    const p = { limit: 60 };
    if (q) p.q = q;
    if (category !== "all") p.category = category;
    if (retailer !== "all") p.retailer = retailer;
    api.get("/deals", { params: p }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [q, category, retailer]);

  useEffect(() => { load(); }, [load]);

  const general = meta?.general_disclosure || "";
  const featured = (items || []).filter((d) => d.featured);
  const all = items || [];

  return (
    <div className="space-y-5" data-testid="deals-page">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10"><Tag className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Mtaa Deals</h1>
          <p className="text-sm text-muted-foreground">Deals worth checking out.</p>
        </div>
      </div>

      {general && (
        <div className="flex items-start gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground" data-testid="deals-general-disclosure">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>{general}</p>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals…" className="border-0 shadow-none focus-visible:ring-0" data-testid="deals-search" />
        </div>
        <div className="flex gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-36" data-testid="deals-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={retailer} onValueChange={setRetailer}>
            <SelectTrigger className="w-36" data-testid="deals-filter-retailer"><SelectValue placeholder="Retailer" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All retailers</SelectItem>{(meta?.retailers || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </form>

      {items === null ? <div className="flex justify-center py-16"><Spinner className="h-7 w-7" /></div> :
        all.length === 0 ? (
          <EmptyState icon={Tag} title="No deals right now." subtitle="Check back soon for new finds from Mtaa." testid="deals-empty" />
        ) : (
          <div className="space-y-6">
            {featured.length > 0 && category === "all" && retailer === "all" && !q && (
              <section>
                <h2 className="mb-3 font-display text-lg font-bold">Featured deals</h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {featured.map((d) => <DealCard key={d.id} deal={d} general={general} />)}
                </div>
              </section>
            )}
            <section>
              <h2 className="mb-3 font-display text-lg font-bold">All deals</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {all.map((d) => <DealCard key={d.id} deal={d} general={general} />)}
              </div>
            </section>
          </div>
        )}
    </div>
  );
}
